import { FC } from "react";
import {
  StyledGroup,
  StyledHeader,
  StyledBody,
  StyledTopDiv,
  StyledList,
  StyledReadMore,
} from "./style";
import { Button } from "../../Common/Button";
import Heading from "../../Common/Heading";
import ScrollBar from "../../Common/Scrollbar";
import SearchForm from "../SearchForm";
import GroupItem from "../GroupItem";
import { useLocation, useNavigate } from "react-router-dom";
import { Communication, ScreenConfig } from "../../../types";
import { useTranslation } from "react-i18next";
import FilterForm from "../FilterForm";
import { CommunicationsEmptyState } from "../EmptyState";
import { Footer } from "../../Layout/Footer";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

type Props = {
  communications: Communication[];
  isFetchingNextPage: boolean;
  search: string;
  hasNextPage: boolean;
  fieldDescription: string;
  filter: string;
  config: ScreenConfig;
  onLoadMore: () => void;
  onSearchChange: (value: string) => void;
  onFilterChange: (e: any) => void;
};

const Group: FC<Props> = ({
  communications,
  isFetchingNextPage,
  search,
  hasNextPage,
  fieldDescription,
  filter,
  config,
  onLoadMore,
  onSearchChange,
  onFilterChange,
}) => {
  const navigate = useNavigate();
  const { hash, search: locationSearch } = useLocation();
  const { t } = useTranslation();

  const handleClick = (id: Communication["Id"]) => {
    navigate(`./${id}${locationSearch}${hash}`);
  };

  return (
    <StyledGroup>
      <StyledHeader>
        <SearchForm value={search} onChange={onSearchChange} />
        <FilterForm value={filter} onChange={onFilterChange} />
      </StyledHeader>
      <ScrollBar top="55px">
        <StyledBody>
          <StyledTopDiv>
            <Heading tt="uppercase" fontWeight={600} mb="0px">
              {fieldDescription}
            </Heading>
          </StyledTopDiv>
          <StyledList>
            {communications.length === 0 && <CommunicationsEmptyState />}
            {communications.map((communication) => (
              <GroupItem
                key={communication.Id}
                color={config.theme.primaryColor}
                name={`${communication.CommunicationDescription} ${communication.Field.Description}`}
                time={dayjs
                  .utc(communication.CreatedAt)
                  .format("DD/MM/YYYY HH:mm")}
                title={communication.Subject}
                excerpt=""
                status={communication.Unreaded ? "unread" : "read"}
                onClick={() => handleClick(communication.Id)}
                numAttachments={communication.Attachments?.length || 0}
              />
            ))}
          </StyledList>
          {hasNextPage && (
            <StyledReadMore>
              <Button
                size="xs"
                color="light"
                fullwidth
                disabled={isFetchingNextPage}
                onClick={onLoadMore}
              >
                {t("components.communications.loadMore")}
              </Button>
            </StyledReadMore>
          )}
          <Footer />
        </StyledBody>
      </ScrollBar>
    </StyledGroup>
  );
};

export default Group;
